import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { mockProjects, mockTasks, mockCompanies, mockUsers } from '../mock/data';
import { 
  Calendar, ChevronLeft, ChevronRight, Filter, Building2, 
  GripVertical, ExternalLink, Clock
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import HeaderGlow from '../components/HeaderGlow';

// Helper function to get days between dates
const getDaysBetween = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
};

// Helper function to get position in timeline
const getPositionInTimeline = (date, timelineStart, totalDays) => {
  const itemDate = new Date(date);
  const start = new Date(timelineStart);
  const daysDiff = Math.ceil((itemDate - start) / (1000 * 60 * 60 * 24));
  return (daysDiff / totalDays) * 100;
};

// Sidebar Timeline - Read-only view of all projects
const AllProjectsTimeline = () => {
  const navigate = useNavigate();
  const { currentColorTheme } = useTheme();
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [viewMode, setViewMode] = useState('month'); // month, quarter
  
  // Calculate timeline range
  const timelineData = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfQuarter = new Date(now.getFullYear(), now.getMonth() + 4, 0);
    
    return {
      start: startOfMonth,
      end: endOfQuarter,
      totalDays: getDaysBetween(startOfMonth, endOfQuarter)
    };
  }, []);

  // Filter projects by company
  const filteredProjects = useMemo(() => {
    if (selectedCompany === 'all') return mockProjects;
    return mockProjects.filter(p => p.companyId === selectedCompany);
  }, [selectedCompany]);

  // Generate month labels
  const monthLabels = useMemo(() => {
    const labels = [];
    let current = new Date(timelineData.start);
    while (current <= timelineData.end) {
      labels.push({
        month: current.toLocaleDateString('en-US', { month: 'short' }),
        year: current.getFullYear(),
        position: getPositionInTimeline(current, timelineData.start, timelineData.totalDays)
      });
      current.setMonth(current.getMonth() + 1);
    }
    return labels;
  }, [timelineData]);

  return (
    <div className="p-8 bg-background min-h-screen">
      {/* Header */}
      <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-indigo-500 via-purple-500 to-pink-500'} -mx-8 -mt-8 p-8`}>
        <h1 className="text-3xl font-bold text-white">Projects Timeline</h1>
        <p className="text-white/80">Overview of all projects across companies</p>
      </div>
      {/* Glowing separator line */}
      <div className="-mx-8">
        <HeaderGlow />
      </div>

      {/* Filters - with top margin */}
      <div className="flex items-center justify-between mb-6 mt-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-muted-foreground" />
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by Company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {mockCompanies.map(company => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          Showing {filteredProjects.length} projects
        </div>
      </div>

      {/* Timeline View */}
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          {/* Timeline Header */}
          <div className="border-b border-border bg-muted/30">
            <div className="flex">
              {/* Project column header */}
              <div className="w-64 flex-shrink-0 p-4 border-r border-border">
                <span className="font-semibold text-foreground">Project</span>
              </div>
              {/* Timeline months */}
              <div className="flex-1 relative h-12">
                {monthLabels.map((label, idx) => (
                  <div
                    key={idx}
                    className="absolute top-0 h-full border-l border-border flex items-center px-2"
                    style={{ left: `${label.position}%` }}
                  >
                    <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                      {label.month} {label.year}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Timeline Rows */}
          <div className="divide-y divide-border">
            {filteredProjects.map(project => {
              const company = mockCompanies.find(c => c.id === project.companyId);
              const startPos = getPositionInTimeline(project.startDate, timelineData.start, timelineData.totalDays);
              const endPos = getPositionInTimeline(project.endDate, timelineData.start, timelineData.totalDays);
              const barWidth = Math.max(endPos - startPos, 2);
              const progressWidth = (project.progress / 100) * barWidth;

              return (
                <div
                  key={project.id}
                  className="flex hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}/details`)}
                >
                  {/* Project Info */}
                  <div className="w-64 flex-shrink-0 p-4 border-r border-border">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: project.color }}
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-foreground truncate">{project.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {company?.name || 'No Company'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Timeline Bar */}
                  <div className="flex-1 relative py-4 px-2">
                    {/* Background grid lines */}
                    {monthLabels.map((label, idx) => (
                      <div
                        key={idx}
                        className="absolute top-0 h-full border-l border-border/30"
                        style={{ left: `${label.position}%` }}
                      />
                    ))}
                    
                    {/* Project Bar Container */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 h-10 rounded-lg overflow-hidden shadow-lg"
                      style={{
                        left: `${Math.max(startPos, 0)}%`,
                        width: `${barWidth}%`,
                        backgroundColor: project.color + '20',
                      }}
                    >
                      {/* Gradient Progress Fill */}
                      <div 
                        className="absolute inset-y-0 left-0 rounded-lg transition-all duration-500"
                        style={{
                          width: `${project.progress}%`,
                          background: `linear-gradient(135deg, ${project.color}ee 0%, ${project.color}88 50%, ${project.color}cc 100%)`,
                          boxShadow: `0 0 20px ${project.color}50`
                        }}
                      />
                      {/* Shimmer effect on progress */}
                      <div 
                        className="absolute inset-y-0 left-0 rounded-lg opacity-30"
                        style={{
                          width: `${project.progress}%`,
                          background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)`,
                        }}
                      />
                      {/* Progress text */}
                      <div className="absolute inset-0 flex items-center px-3">
                        <span className="text-sm font-semibold text-white drop-shadow-md">
                          {project.progress}%
                        </span>
                      </div>
                    </div>

                    {/* Today marker - Solid line design */}
                    <div
                      className="absolute top-0 h-full z-20"
                      style={{
                        left: `${getPositionInTimeline(new Date(), timelineData.start, timelineData.totalDays)}%`,
                      }}
                    >
                      {/* Solid red line */}
                      <div className="h-full w-[2px] bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredProjects.length === 0 && (
            <div className="p-12 text-center text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No projects found for the selected company</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-8 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="w-[2px] h-5 bg-red-500" />
          <span>Today</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-10 h-4 rounded bg-gradient-to-r from-primary/80 to-primary/40 shadow-sm" />
          <span>Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-10 h-4 rounded bg-primary/20" />
          <span>Remaining</span>
        </div>
      </div>
    </div>
  );
};

// Project Timeline - Interactive Gantt chart
const ProjectGanttTimeline = ({ projectId }) => {
  const navigate = useNavigate();
  const project = mockProjects.find(p => p.id === projectId);
  const projectTasks = mockTasks.filter(t => t.projectId === projectId);
  
  // State for drag operations (UI only for now)
  const [draggedTask, setDraggedTask] = useState(null);

  // Calculate timeline range based on project dates (moved before conditional return)
  const timelineData = useMemo(() => {
    if (!project) return { start: new Date(), end: new Date(), totalDays: 30 };
    
    const start = new Date(project.startDate);
    start.setDate(start.getDate() - 7); // Add buffer
    const end = new Date(project.endDate);
    end.setDate(end.getDate() + 7); // Add buffer
    
    return {
      start,
      end,
      totalDays: getDaysBetween(start, end)
    };
  }, [project]);

  // Generate week labels (moved before conditional return)
  const weekLabels = useMemo(() => {
    if (!project) return [];
    
    const labels = [];
    let current = new Date(timelineData.start);
    while (current <= timelineData.end) {
      labels.push({
        date: new Date(current),
        label: current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        position: getPositionInTimeline(current, timelineData.start, timelineData.totalDays)
      });
      current.setDate(current.getDate() + 7);
    }
    return labels;
  }, [project, timelineData]);

  const handleDragStart = (task) => {
    setDraggedTask(task);
  };

  if (!project) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Project not found
      </div>
    );
  }

  const handleDragEnd = () => {
    if (draggedTask) {
      alert(`Task "${draggedTask.title}" would be rescheduled. Backend integration required.`);
      setDraggedTask(null);
    }
  };

  return (
    <div className="p-8 bg-background min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 -mx-8 -mt-8 p-8 mb-8">
        <Button 
          variant="ghost" 
          className="text-white/80 hover:text-white hover:bg-white/10 mb-4"
          onClick={() => navigate(`/projects/${projectId}/details`)}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Project
        </Button>
        <h1 className="text-3xl font-bold text-white">{project.name} - Timeline</h1>
        <p className="text-indigo-100">Drag tasks to reschedule</p>
      </div>

      {/* Project Progress */}
      <Card className="mb-6 bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color }} />
              <div>
                <p className="font-medium text-foreground">{project.name}</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(project.startDate).toLocaleDateString()} - {new Date(project.endDate).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-2xl font-bold text-foreground">{project.progress}%</p>
                <p className="text-sm text-muted-foreground">Complete</p>
              </div>
              <div className="w-32">
                <Progress value={project.progress} className="h-2" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gantt Chart */}
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          {/* Timeline Header */}
          <div className="border-b border-border bg-muted/30">
            <div className="flex">
              {/* Task column header */}
              <div className="w-72 flex-shrink-0 p-4 border-r border-border">
                <span className="font-semibold text-foreground">Task</span>
              </div>
              {/* Timeline weeks */}
              <div className="flex-1 relative h-12 overflow-hidden">
                {weekLabels.map((week, idx) => (
                  <div
                    key={idx}
                    className="absolute top-0 h-full border-l border-border flex items-center px-2"
                    style={{ left: `${week.position}%` }}
                  >
                    <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                      {week.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Task Rows */}
          <div className="divide-y divide-border">
            {projectTasks.map(task => {
              const assignee = mockUsers.find(u => u.id === task.assigneeId);
              const startPos = getPositionInTimeline(task.startDate, timelineData.start, timelineData.totalDays);
              const endPos = getPositionInTimeline(task.dueDate, timelineData.start, timelineData.totalDays);
              const barWidth = Math.max(endPos - startPos, 3);
              
              const statusColor = task.status === 'completed' ? '#10b981' : 
                                  task.status === 'in-progress' ? '#3b82f6' : '#94a3b8';

              return (
                <div
                  key={task.id}
                  className="flex hover:bg-muted/30 transition-colors"
                  draggable
                  onDragStart={() => handleDragStart(task)}
                  onDragEnd={handleDragEnd}
                >
                  {/* Task Info */}
                  <div className="w-72 flex-shrink-0 p-4 border-r border-border">
                    <div className="flex items-center gap-3">
                      <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                      <div className="min-w-0 flex-1">
                        <p className={`font-medium text-foreground truncate ${task.status === 'completed' ? 'line-through opacity-60' : ''}`}>
                          {task.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Avatar className="w-5 h-5">
                            <AvatarImage src={assignee?.avatar} />
                            <AvatarFallback className="text-xs">{assignee?.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-muted-foreground truncate">{assignee?.name}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Timeline Bar */}
                  <div className="flex-1 relative py-3 px-2">
                    {/* Background grid lines */}
                    {weekLabels.map((week, idx) => (
                      <div
                        key={idx}
                        className="absolute top-0 h-full border-l border-border/30"
                        style={{ left: `${week.position}%` }}
                      />
                    ))}
                    
                    {/* Task Bar with Gradient Fill */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 h-9 rounded-lg overflow-hidden cursor-move shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl"
                      style={{
                        left: `${Math.max(startPos, 0)}%`,
                        width: `${barWidth}%`,
                        backgroundColor: statusColor + '20',
                      }}
                    >
                      {/* Gradient Progress Fill */}
                      <div 
                        className="absolute inset-y-0 left-0 rounded-lg transition-all duration-300"
                        style={{
                          width: task.status === 'completed' ? '100%' : 
                                 task.status === 'in-progress' ? `${(task.loggedHours / task.estimatedHours) * 100}%` : '0%',
                          background: `linear-gradient(135deg, ${statusColor}ee 0%, ${statusColor}88 50%, ${statusColor}cc 100%)`,
                          boxShadow: `0 0 15px ${statusColor}40`
                        }}
                      />
                      {/* Shimmer effect */}
                      <div 
                        className="absolute inset-y-0 left-0 rounded-lg opacity-20"
                        style={{
                          width: task.status === 'completed' ? '100%' : 
                                 task.status === 'in-progress' ? `${(task.loggedHours / task.estimatedHours) * 100}%` : '0%',
                          background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)`,
                        }}
                      />
                      {/* Task info */}
                      <div className="absolute inset-0 flex items-center px-2">
                        <div className="flex items-center gap-1 text-xs font-medium text-white drop-shadow-md">
                          <Clock className="w-3 h-3" />
                          <span>{task.estimatedHours}h</span>
                        </div>
                      </div>
                    </div>

                    {/* Today marker - Creative design */}
                    <div
                      className="absolute top-0 h-full z-20 flex flex-col items-center"
                      style={{
                        left: `${getPositionInTimeline(new Date(), timelineData.start, timelineData.totalDays)}%`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      {/* Top diamond */}
                      <div className="w-2.5 h-2.5 bg-gradient-to-br from-red-400 to-pink-500 rotate-45 shadow-lg shadow-red-500/50" />
                      {/* Glowing line */}
                      <div className="flex-1 w-0.5 bg-gradient-to-b from-red-500 via-pink-500 to-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]" />
                      {/* Bottom diamond */}
                      <div className="w-2.5 h-2.5 bg-gradient-to-br from-pink-500 to-red-400 rotate-45 shadow-lg shadow-red-500/50" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {projectTasks.length === 0 && (
            <div className="p-12 text-center text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No tasks found for this project</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-8 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="flex flex-col items-center h-5">
            <div className="w-2 h-2 bg-gradient-to-br from-red-400 to-pink-500 rotate-45" />
            <div className="flex-1 w-0.5 bg-gradient-to-b from-red-500 to-pink-500" />
          </div>
          <span>Today</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-4 rounded bg-gradient-to-r from-green-500/90 to-green-500/50 shadow-sm" />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-4 rounded bg-gradient-to-r from-blue-500/90 to-blue-500/50 shadow-sm" />
          <span>In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-4 rounded bg-slate-400/20" />
          <span>To Do</span>
        </div>
      </div>
    </div>
  );
};

// Main Timeline component - determines which view to show
const Timeline = () => {
  const { projectId } = useParams();
  
  // If projectId is provided, show interactive Gantt chart
  // Otherwise, show read-only all projects timeline
  if (projectId) {
    return <ProjectGanttTimeline projectId={projectId} />;
  }
  
  return <AllProjectsTimeline />;
};

export default Timeline;
